/* eslint-disable react/jsx-no-undef */
import { useEffect, useState } from "react";
import { Steps, Progress, Row, Col, Typography, Space, Tooltip } from "antd";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
// import dayjs from "dayjs";

import ActivityManager from "./ActivityManager";
import SubActivityManager_v2 from "./SubActivityManager_v2";
import { ACTIVITY_STATES, isTerminalState } from "./activityStates";

const { Step } = Steps;
const { Text } = Typography;

const STATUS_LEGEND = [
  { key: "Created", label: ACTIVITY_STATES.Created.label },
  { key: "Started", label: ACTIVITY_STATES.Started.label },
  { key: "Completed", label: "Completada / Omitida / Finalizada" },
];

/**
 * Componente principal para gestionar actividades con interfaz de Steps mejorada
 * @param {Object} props - Propiedades del componente
 * @param {Array} props.data - Array de actividades a gestionar
 * @param {number} props.currentOrder - Orden actual de la actividad
 * @param {Function} props.onChange - Función callback cuando cambia el paso
 * @param {Function} props.consultaACTIVIDADES - Función para consultar actividades
 * @returns {JSX.Element} Componente de gestión de actividades
 */
export default function GestorActividades({
  data = [],
  currentOrder = 0,
  onChange,
  consultaACTIVIDADES,
  fetchData,
  onActivityUpdate,
}) {
  function filtrarYAgruparPorOrder(arrayDeObjetos) {
    const objetosConEnteroOrder = [];

    arrayDeObjetos.forEach((objeto) => {
      const orderComoEntero = parseFloat(objeto.order);

      if (!isNaN(orderComoEntero) && orderComoEntero % 1 === 0) {
        // Entero
        objetosConEnteroOrder.push(objeto);
      }
    });

    // Ordenar de menor a mayor por order
    objetosConEnteroOrder.sort(
      (a, b) => parseFloat(a.order) - parseFloat(b.order)
    );

    const objetosAgrupadosPorDecimal = arrayDeObjetos.reduce(
      (agrupados, objeto) => {
        const orderComoEntero = parseInt(objeto.order);
        if (!isNaN(orderComoEntero)) {
          const orderDecimal =
            orderComoEntero % 1 !== 0 ? orderComoEntero % 1 : 0;
          const orderRedondeado = orderComoEntero - orderDecimal;

          if (!agrupados[orderRedondeado]) {
            agrupados[orderRedondeado] = [];
          }

          agrupados[orderRedondeado].push(objeto);
        }

        return agrupados;
      },
      {}
    );

    return [objetosConEnteroOrder, objetosAgrupadosPorDecimal];
  }

  const [objetosConEnteroOrder, objetosAgrupadosPorDecimal] =
    filtrarYAgruparPorOrder(data);

  const [currentStepIndex, setCurrentStepIndex] = useState(currentOrder);
  const [hasInitialized, setHasInitialized] = useState(false);

  const [, setCurrentStep] = useState(objetosConEnteroOrder[currentStepIndex]);
  const [currentSubActividadesStep, setcurrentSubActividadestep] = useState(
    objetosAgrupadosPorDecimal[currentStepIndex] || []
  );

  // Un paso está pendiente mientras cualquiera de sus actividades siga sin
  // estado terminal: la principal (5) o sus sub-actividades (5.1, 5.2...).
  const stepIsPending = (step, grupos = objetosAgrupadosPorDecimal) => {
    // "Omitir" la actividad principal salta el paso entero: no se vuelve a
    // marcar como pendiente aunque el backend deje sub-actividades sin cerrar.
    if (step.state === "Skipped") return false;

    const grupo = grupos[parseInt(step.order)] || [step];
    return grupo.some((act) => !isTerminalState(act.state));
  };

  /**
   * Función para encontrar el paso actual basado en el estado de las actividades
   * @returns {number} El orden del paso donde quedó el protocolo
   */
  const findCurrentProtocolStep = () => {
    if (objetosConEnteroOrder.length === 0) return 0;

    // Buscar el primer paso que esté pendiente o en proceso.
    // Ojo: hay que mirar el grupo completo, no solo la actividad entera.
    // Mirando solo `step.state`, al completar la 5 el paso quedaba en estado
    // terminal y el gestor saltaba al 6 ignorando la 5.1 que el backend acaba
    // de crear.
    const pendingStep = objetosConEnteroOrder.find((step) =>
      stepIsPending(step)
    );

    if (pendingStep) {
      return parseInt(pendingStep.order);
    }

    // Si no hay pasos pendientes, buscar el último paso completado
    const lastCompletedStep = objetosConEnteroOrder
      .slice()
      .reverse()
      .find((step) => isTerminalState(step.state));

    if (lastCompletedStep) {
      return parseInt(lastCompletedStep.order);
    }

    // Si no hay ningún paso con estado, ir al primer paso
    return parseInt(objetosConEnteroOrder[0]?.order || 0);
  };

  /**
   * Función para navegar automáticamente al paso donde quedó el protocolo
   */
  const navigateToCurrentProtocolStep = () => {
    const targetOrder = findCurrentProtocolStep();

    // Solo navegar si es diferente al paso actual
    if (targetOrder !== currentOrder) {
      onChange?.(targetOrder);
    }
  };

  /**
   * Función para calcular el progreso general del protocolo
   * @returns {number} Porcentaje de progreso (0-100)
   */
  const calculateProgress = () => {
    if (objetosConEnteroOrder.length === 0) return 0;

    // Mismo criterio que la navegación: un paso cuenta como completado solo
    // cuando ni la actividad principal ni sus sub-actividades siguen abiertas.
    const completedSteps = objetosConEnteroOrder.filter(
      (step) => !stepIsPending(step)
    ).length;

    return Math.round((completedSteps / objetosConEnteroOrder.length) * 100);
  };

  /**
   * Función para obtener el estado del progreso
   * @returns {string} Estado del progreso (success, exception, normal)
   */
  const getProgressStatus = () => {
    const progress = calculateProgress();

    if (progress === 100) return "success";
    if (progress === 0) return "exception";
    return "active";
  };

  /**
   * Función para manejar la actualización de actividades
   * @param {Object} activity - Actividad actualizada
   * @param {string} newState - Nuevo estado de la actividad
   */
  const handleActivityUpdate = async (activity, newState) => {
    try {
      // Avisar al componente padre para que sincronice su copia de los datos
      // de inmediato (esto se sobrescribirá con la respuesta fresca del
      // servidor más abajo, pero evita una pantalla desactualizada mientras
      // esa consulta está en curso).
      onActivityUpdate?.(activity, newState);

      // Solo tiene sentido evaluar el avance de paso cuando la actividad
      // quedó en un estado terminal.
      if (!isTerminalState(newState)) return;

      // Volver a consultar el servidor antes de decidir si se avanza de
      // paso. Esto es indispensable cuando el paso tiene sub-actividades
      // (5, 5.1, 5.2...): el backend puede generar una sub-actividad nueva
      // como consecuencia de esta acción, y si decidiéramos con los datos
      // locales (todavía desactualizados) el protocolo saltaría de una vez
      // al paso 6 en lugar de mostrar primero la 5.1.
      const freshItems = consultaACTIVIDADES
        ? await consultaACTIVIDADES()
        : null;

      const [freshSteps, freshGrouped] = Array.isArray(freshItems)
        ? filtrarYAgruparPorOrder(freshItems)
        : [objetosConEnteroOrder, objetosAgrupadosPorDecimal];

      const currentStepActivities = freshGrouped[parseInt(currentOrder)] || [];
      const allCompleted =
        currentStepActivities.length > 0 &&
        currentStepActivities.every((act) => {
          // Si ya tenemos datos frescos del servidor, confiamos en su estado
          // tal cual; si no, simulamos el cambio recién aplicado sobre la
          // actividad correspondiente (los datos locales aún no lo reflejan).
          const state = Array.isArray(freshItems)
            ? act.state
            : act.protocol_activity_subactivity ===
              activity.protocol_activity_subactivity
            ? newState
            : act.state;
          return isTerminalState(state);
        });

      // "Omitir" sobre la actividad principal (order entero) salta el paso
      // completo, sub-actividades incluidas: es la única acción que avanza sin
      // esperar a que el paso quede cerrado. Completarla, en cambio, deja el
      // paso abierto para atender la 5.1, 5.2, ...
      const esActividadPrincipal = parseFloat(activity.order) % 1 === 0;
      const omitePasoCompleto = newState === "Skipped" && esActividadPrincipal;

      if (!allCompleted && !omitePasoCompleto) return;

      const stepIndex = freshSteps.findIndex(
        (step) => parseInt(step.order) === parseInt(currentOrder)
      );

      if (stepIndex !== -1 && stepIndex < freshSteps.length - 1) {
        const nextStep = freshSteps[stepIndex + 1];
        onChange?.(parseInt(nextStep.order));
      }
    } catch (error) {
      console.error("Error al actualizar actividad:", error);
    }
  };

  // Efecto para navegación automática al abrir el gestor
  useEffect(() => {
    if (objetosConEnteroOrder.length > 0 && !hasInitialized) {
      navigateToCurrentProtocolStep();
      setHasInitialized(true);
    }
  }, [objetosConEnteroOrder, hasInitialized]);

  useEffect(() => {
    const index = objetosConEnteroOrder.findIndex(
      (step) => parseInt(step.order) === parseInt(currentOrder)
    );

    if (index !== -1) {
      const newStep = objetosConEnteroOrder[index];
      const newSubSteps =
        objetosAgrupadosPorDecimal[parseInt(newStep.order)] || [];

      // Solo actualiza si realmente cambian los valores
      setCurrentStepIndex((prev) => (prev !== index ? index : prev));
      setCurrentStep((prev) =>
        prev?.order !== newStep.order ? newStep : prev
      );
      setcurrentSubActividadestep((prev) =>
        JSON.stringify(prev) !== JSON.stringify(newSubSteps)
          ? newSubSteps
          : prev
      );
    }
  }, [currentOrder, objetosConEnteroOrder, objetosAgrupadosPorDecimal]);

  return (
    <div style={{ padding: "24px" }}>
      <Steps
        type="navigation"
        current={currentStepIndex}
        size="small"
        onChange={(index) => {
          const step = objetosConEnteroOrder[index];
          onChange?.(parseInt(step.order));
        }}
      >
        {objetosConEnteroOrder.map((step) => (
          <Step
            key={step.order}
            title={step.order}
            status="process"
            icon={
              <Tooltip
                title={`Paso ${step.order} · ${
                  (ACTIVITY_STATES[step.state] || ACTIVITY_STATES.Created)
                    .label
                }`}
              >
                <div
                  style={{
                    backgroundColor: (
                      ACTIVITY_STATES[step.state] || ACTIVITY_STATES.Created
                    ).dot,
                    borderRadius: "50%",
                    width: 12,
                    height: 12,
                  }}
                />
              </Tooltip>
            }
          />
        ))}
      </Steps>

      {/* Leyenda de los puntos de estado del Step superior */}
      <Space size="middle" style={{ marginTop: 8, flexWrap: "wrap" }}>
        {STATUS_LEGEND.map((item) => (
          <Space key={item.key} size={6} align="center">
            <span
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: ACTIVITY_STATES[item.key].dot,
              }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {item.label}
            </Text>
          </Space>
        ))}
      </Space>

      {/* Barra de progreso del protocolo */}
      <div style={{ marginTop: 16, marginBottom: 24 }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
          <Col>
            <Text strong>Progreso del protocolo</Text>
          </Col>
          <Col>
            <Text type="secondary">{calculateProgress()}% completado</Text>
          </Col>
        </Row>
        <Progress
          percent={calculateProgress()}
          status={getProgressStatus()}
          strokeColor="#7AC142"
          strokeWidth={8}
          showInfo={false}
          style={{
            borderRadius: "4px",
          }}
        />
        <Row justify="space-between" style={{ marginTop: 8 }}>
          <Col>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              Paso actual: {currentStepIndex + 1} de{" "}
              {objetosConEnteroOrder.length}
            </Text>
          </Col>
          <Col>
            <Text type="secondary" style={{ fontSize: "12px" }}>
              {objetosConEnteroOrder.filter((step) => !stepIsPending(step))
                .length}{" "}
              de {objetosConEnteroOrder.length} pasos completados
            </Text>
          </Col>
        </Row>
      </div>

      <div
        style={{
          marginTop: 24,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Tooltip title="Paso anterior">
          <ArrowLeftOutlined
            style={{
              fontSize: 24,
              cursor: currentStepIndex > 0 ? "pointer" : "not-allowed",
              opacity: currentStepIndex > 0 ? 1 : 0.3,
            }}
            onClick={() =>
              currentStepIndex > 0 &&
              onChange?.(
                parseInt(objetosConEnteroOrder[currentStepIndex - 1].order)
              )
            }
          />
        </Tooltip>

        {currentSubActividadesStep.length > 1 ? (
          <SubActivityManager_v2
            Activities={currentSubActividadesStep}
            consultaACTIVIDADES={consultaACTIVIDADES}
            onActivityUpdate={handleActivityUpdate}
            fetchData={fetchData}
          />
        ) : (
          currentSubActividadesStep.map((activity) => (
            <ActivityManager
              key={activity.protocol_activity_subactivity}
              activity={activity}
              subactivitys={currentSubActividadesStep}
              consultaACTIVIDADES={consultaACTIVIDADES}
              onActivityUpdate={handleActivityUpdate}
              fetchData={fetchData}
            />
          ))
        )}

        <Tooltip title="Paso siguiente">
          <ArrowRightOutlined
            style={{
              fontSize: 24,
              cursor:
                currentStepIndex < objetosConEnteroOrder.length - 1
                  ? "pointer"
                  : "not-allowed",
              opacity:
                currentStepIndex < objetosConEnteroOrder.length - 1 ? 1 : 0.3,
            }}
            onClick={() =>
              currentStepIndex < objetosConEnteroOrder.length - 1 &&
              onChange?.(
                parseInt(objetosConEnteroOrder[currentStepIndex + 1].order)
              )
            }
          />
        </Tooltip>
      </div>
    </div>
  );
}
